import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { addSpeculationRules } from 'utils/common';
import Listingheader from 'routes/home/components/common/ListingheaderV2';
import NewStoryCard from 'components_v2/desktop/NewStoryCard';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import TOIPlusWidget from 'components_v3/desktop/TOIPlusWidget/TOIPlusWidget';
import AdCaller from 'helpers/ads/adCaller';
import InViewObserver from 'components/InViewObserver/InViewObserver';
import GamesCard from 'routes/home/components/common/GamesCard/GamesCard';
import RefreshStories from './RefershStories';
import RenderInserts from '../../../routes/home/components/desktop/RenderInsertsV2/RenderInserts';

import s from './HomePageTopLayout.scss';

const renderInserts = (
  inserts,
  isPrime,
  userData,
  isLoggedIn,
  isInPageDock,
  showToiPlusEntryPoints,
) => (
  <ErrorBoundary>
    <RenderInserts
      data={inserts}
      isPrime={isPrime}
      isLoggedIn={isLoggedIn}
      userData={userData}
      isInPageDock={isInPageDock}
      showToiPlusEntryPoints={showToiPlusEntryPoints}
    />
  </ErrorBoundary>
);

const HomePageTopLayout = ({
  sectionData,
  isPrime,
  isLoggedIn,
  userData,
  isInPageDock,
  className,
  geoCityData,
  isGccPage,
}) => {
  useEffect(() => {
    addSpeculationRules('speculationContainer');
  }, []);

  const { items = [] } = sectionData || {};
  const [showHoveredVideo, setShowHoveredVideo] = useState(false);
  const inserts =
    Array.isArray(items) && items.length > 2 && items[2]
      ? items[2].inserts
      : [];
  const belowInserts =
    Array.isArray(items) && items.length > 2 && items[2]
      ? items[2].below_inserts
      : [];

  const renderSectionItems = ({
    section,
    cardType = '',
    gridName,
    leadImage = 0,
  }) => {
    if (!section.items || !Array.isArray(section.items)) {
      return null;
    }

    return section.items.map((item, itemIndex) => {
      if (item.tn === 'ad') {
        return (
          <li
            key={`ad-${itemIndex}`}
            className={`${s.cardListItem} ${s.adItem}`}
          >
            <AdCaller data={item} />
          </li>
        );
      }
      if (item.tn === 'games_card_widget_Slider') {
        return (
          <ErrorBoundary>
            <GamesCard
              gaCategory={{
                ga4Type: 'home_widget',
                ga4Label: 'homepage',
                ga4Type2: 'main_line_up_concept_8',
                ga4Category2: 'game_widget_click',
                ga4Label2: 'rightgrid_',
              }}
              widgetUsedOn="homepage"
              sectionData={item}
            />
          </ErrorBoundary>
        );
      }

      if (item.tn === 'TOIPlusWidget') {
        return (
          <ErrorBoundary>
            <li key={`toipluswidget-${itemIndex}`} className={s.cardListItem}>
              <TOIPlusWidget
                item={item}
                isPrime={isPrime}
                isUShomepage={false}
                small
                ga4Type={sectionData.ga4Type || 'main_line_up'}
              />
            </li>
          </ErrorBoundary>
        );
      }

      let templateName = 'articleshow';

      if (item.tn === 'video') {
        templateName = 'videoshow';
      } else if (item.tn === 'news') {
        templateName = 'articleshow';
      } else if (item.tn) {
        templateName = item.tn;
      }

      if (item.cs === 'prime') {
        item.ga4SubLabel = 'toiplus';
      }

      return (
        <li key={`story-${item.id || itemIndex}`} className={s.cardListItem}>
          <ErrorBoundary>
            <InViewObserver
              inViewCallback={() => {
                if (itemIndex === 0) {
                  setShowHoveredVideo(true);
                }
              }}
            >
              <NewStoryCard
                title={item.hl}
                msid={item.id}
                wu={item.wu}
                imagesize={item.imagesize}
                target={item.target}
                ga4Category="story_click"
                ga4Label={`${gridName}_${templateName}`}
                ga4Type={sectionData.ga4Type || 'main_line_up'}
                ga4SubLabel={item.ga4SubLabel}
                gaIndex={parseInt(item.gaindex, 10)}
                shouldNotlazyloadImage={itemIndex < leadImage}
                ag={item.ag}
                createdTime={item.createdTime}
                videoTime={item.du}
                des={item.imagecaptn}
                cardType={item.cardType || cardType}
                tn={item.tn}
                slikeId={item.playerId}
                hasVideo={item.hasVideo}
                syn={item.des}
                imagelist={item.imagelist}
                sectionNameTop={item.section}
                cs={item.cs}
                showHoveredVideo={itemIndex === 0 && showHoveredVideo}
              />
            </InViewObserver>
          </ErrorBoundary>
        </li>
      );
    });
  };

  const renderSection = (section, sectionIndex) => {
    switch (section.tn) {
      case 'col_1':
        return (
          <section key={`lead-section-${sectionIndex}`} className={s.columnOne}>
            <ul className={s.cardListWrap}>
              {renderSectionItems({
                section,
                gridName: 'LeftGrid',
              })}
            </ul>
          </section>
        );

      case 'col_2':
        return (
          <section
            key={`column-section-${sectionIndex}`}
            className={s.columnTwo}
          >
            <ul className={s.cardListWrap}>
              {renderSectionItems({
                section,
                gridName: 'CenterGrid',
                leadImage: 1,
              })}
            </ul>
          </section>
        );

      case 'col_3':
        return (
          <section
            key={`default-section-${sectionIndex}`}
            className={s.columnThree}
          >
            <div className={s.adWrap}>
              {renderInserts(
                inserts,
                isPrime,
                section,
                userData,
                isLoggedIn,
                isInPageDock,
                true,
              )}
            </div>
            {section.name &&
              section.link && (
                <Listingheader
                  headingname={section.name}
                  headingLink={section.link}
                  target={section.target}
                  ga4Type={section.ga4Type || ''}
                  ga4Label={section.ga4Label || ''}
                  ga4Category={section.ga4Category || ''}
                />
              )}
            <ul className={s.cardListWrap}>
              {renderSectionItems({
                section,
                gridName: 'RightGrid',
                cardType: 'card_4_2',
              })}
            </ul>
            {renderInserts(
              belowInserts,
              isPrime,
              section,
              userData,
              isLoggedIn,
              isInPageDock,
            )}
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div id="adsdivLyr" className="adsdivlyr" />
      <div id="tileyeDiv" />
      <div id="netspidersosh" name="netspidersosh" className="adsdivlyr" />
      {items &&
        Array.isArray(items) &&
        items.length > 0 && (
          <RefreshStories
            sectionData={sectionData}
            geoCityData={geoCityData}
            isPrime={isPrime}
            isGccPage={isGccPage}
          />
        )}
      <div
        className={`${s.homePageTopLayout} ${
          s[className]
        } speculationContainer`}
      >
        {items &&
          Array.isArray(items) &&
          items.map((section, sectionIndex) =>
            renderSection(section, sectionIndex),
          )}
      </div>
    </>
  );
};

HomePageTopLayout.propTypes = {
  items: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        tn: PropTypes.string,
        items: PropTypes.arrayOf(PropTypes.object),
        inserts: PropTypes.arrayOf(PropTypes.object),
      }),
    ),
    ga4Type: PropTypes.string,
  }),
};

HomePageTopLayout.defaultProps = {
  items: {
    items: [],
    ga4Type: '',
  },
};

export default HomePageTopLayout;
